/* eslint-disable react-hooks/exhaustive-deps */
// Table.js (FULL, FIXED)
import React, { forwardRef, useImperativeHandle, useState, useEffect } from "react";
import PivotTableUI from "./SafePivotTableUI";
import "react-pivottable/pivottable.css";
import "./pivottable-theme.css";
import { Card } from "@lightspeed/flame/Card";
import styled from "@emotion/styled";
import TableRenderers from "./TableRenderers";
import * as XLSX from "xlsx"; // handling excel export
import { PivotData } from "./Utilities";

const StyledCard = styled(Card)`
  flex-grow: 1;
  position: relative;
  overflow: auto;
  min-height: 720px;
  border-radius: 28px;
  border: 1px solid rgba(133, 150, 179, 0.16);
  background:
    radial-gradient(circle at top right, rgba(245, 166, 35, 0.08), transparent 22%),
    linear-gradient(180deg, rgba(255, 255, 255, 0.98) 0%, rgba(247, 250, 253, 0.98) 100%);
  box-shadow:
    0 26px 58px rgba(15, 23, 42, 0.12),
    0 8px 20px rgba(15, 23, 42, 0.06);

  @media (max-width: 1100px) {
    min-height: 560px;
  }
`;

const Container = styled.div`
  position: absolute;
  inset: 0;
  padding: 20px;

  .pvtUi {
    width: 100%;
  }

  .pvtRenderers,
  .pvtVals,
  .pvtAxisContainer {
    border-radius: 18px;
  }

  .pvtRendererArea {
    padding-top: 12px;
  }
`;
const ReportInfoIcon = styled.button`
  width: 210px;
  min-height: 26px;
  border-radius: 8px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border: 1px solid #e6bd48;
  background: #ffe08a;
  color: #4a3a08;
  font-size: 13px;
  font-weight: 800;
  line-height: 1;
  cursor: pointer;
  user-select: none;
  padding: 5px 14px;
  appearance: none;

  &:hover {
    background: #ffd76b;
  }

  &:focus-visible {
    outline: 3px solid rgba(245, 197, 66, 0.4);
    outline-offset: 2px;
  }
`;

const ReportActionsHeader = styled.div`
  position: absolute;
  top: 5px;
  left: 30px;
  z-index: 5;
  display: flex;
  align-items: center;
  justify-content: flex-start;
`;

// ---- helpers ----
const downloadText = (filename, content) => {
  const blob = new Blob([content], { type: "text/plain;charset=utf-8" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
};

const safeArray = (v) => (Array.isArray(v) ? v : []);
const normalizePivotValue = (value) => {
  if (value === undefined) return null;
  if (value === null) return null;
  if (Array.isArray(value)) {
    return value.map((item) => normalizePivotValue(item)).join(", ");
  }
  if (value instanceof Date) {
    return value.toISOString();
  }
  if (typeof value === "object") {
    try {
      return JSON.stringify(value);
    } catch (error) {
      return String(value);
    }
  }
  return value;
};

const normalizeExportNumber = (value) => {
  const number = Number(value);
  if (!Number.isFinite(number)) return 0;
  if (Math.abs(number) < 1e-10) return 0;
  return Number(number.toFixed(10));
};

const isQBDesktopMode = (name, data) => {
  const n = (name || "").toLowerCase();

  // prepoznaj ime: qb desktop / qb_desktop / qb-desktop / qbdesktop / + dodatni tekst
  if (/qb[\s\-_]*desktop/.test(n)) return true;

  // fallback: prepoznaj po sadržaju row linija
  const list = Array.isArray(data) ? data : [];
  let hits = 0;
  for (const r of list) {
    const line = (r?.row ?? "").toString();
    if (/^!?(TRNS|SPL|ENDTRNS)\b/i.test(line)) {
      hits++;
      if (hits >= 2) return true; // dvije "QB" linije dovoljne za detekciju
    }
  }
  return false;
};

const isSageMode = (name, data) => {
  const list = Array.isArray(data) ? data : [];
  if (!list.length) return false;

  // Sage mode ako postoji kolona "sage_row"
  const first = list[0] || {};
  return Object.prototype.hasOwnProperty.call(first, "sage_row");
};

const hasRowField = (fields) =>
  (Array.isArray(fields) ? fields : []).some(
    (field) => String(field || "").trim().toLowerCase() === "row"
  );

const isTotalsLine = (value) => /^totals?$/i.test(String(value || "").trim());

const nonSummableNumericFields = new Set([
  "id",
  "account_id",
  "gl_number",
  "transaction_id",
]);

const shouldTotalFieldName = (attrName) => {
  const normalizedName = String(attrName || "")
    .trim()
    .toLowerCase()
    .replace(/[\s-]+/g, "_");

  return (
    normalizedName === "debit" ||
    normalizedName === "credit" ||
    normalizedName === "debit_credit_amount" ||
    normalizedName.includes("amount") ||
    normalizedName.includes("discount") ||
    normalizedName.includes("sale") ||
    normalizedName.includes("tax") ||
    normalizedName.includes("tip") ||
    normalizedName.startsWith("total")
  );
};

const parseDisplayNumber = (value) => {
  if (value === null || value === undefined || value === "") return null;
  if (typeof value === "number") return Number.isFinite(value) ? value : null;

  const rawValue = String(value).trim();
  if (!rawValue || /^(null|undefined)$/i.test(rawValue)) return null;

  const isNegativeAccountingValue = /^\(.*\)$/.test(rawValue);
  const normalized = rawValue
    .replace(/[,$\s]/g, "")
    .replace(/[()]/g, "");

  if (!normalized || /^-?\d{4}-\d{2}-\d{2}$/.test(normalized)) return null;

  const parsed = Number(normalized);
  if (!Number.isFinite(parsed)) return null;

  return isNegativeAccountingValue ? -parsed : parsed;
};

const shouldTotalRowAttr = (attrName, values) => {
  if (nonSummableNumericFields.has(attrName) || !shouldTotalFieldName(attrName)) {
    return false;
  }

  const populatedValues = values.filter(
    (value) =>
      value !== null &&
      value !== undefined &&
      String(value).trim() !== "" &&
      !/^(null|undefined)$/i.test(String(value).trim())
  );

  return (
    populatedValues.length > 0 &&
    populatedValues.every((value) => parseDisplayNumber(value) !== null)
  );
};

const getFilteredRawRecords = (pivotData) => {
  const records = [];

  PivotData.forEachRecord(
    pivotData.props.data,
    pivotData.props.derivedAttributes,
    (record) => {
      if (pivotData.filter(record)) {
        records.push(record);
      }
    }
  );

  return records;
};

const getRowAttrTotals = (pivotData, rowAttrs) => {
  const rawRecords = getFilteredRawRecords(pivotData);

  return rowAttrs.reduce((totals, attrName) => {
    const values = rawRecords.map((record) =>
      attrName in record ? record[attrName] : null
    );

    if (shouldTotalRowAttr(attrName, values)) {
      totals[attrName] = values.reduce((sum, value) => {
        const numberValue = parseDisplayNumber(value);
        return numberValue === null ? sum : sum + numberValue;
      }, 0);
    }

    return totals;
  }, {});
};

const stripTotalsFromTextLines = (lines) => {
  if (!Array.isArray(lines) || lines.length === 0) return [];

  try {
    const content = lines.join("\r\n");
    const delimiter =
      lines.some((line) => String(line || "").includes("\t")) &&
      !lines.some((line) => String(line || "").includes(","))
        ? "\t"
        : ",";
    const workbook = XLSX.read(content, { type: "string", raw: true });
    const sheet = workbook.Sheets[workbook.SheetNames[0]];
    const table = XLSX.utils.sheet_to_json(sheet, {
      header: 1,
      defval: "",
      raw: false,
    });

    if (!table.length) return lines;

    const lastRow = table[table.length - 1] || [];
    if (lastRow.some((cell) => isTotalsLine(cell))) {
      table.pop();
    }

    const header = table.find((row) =>
      Array.isArray(row) && row.some((cell) => String(cell || "").trim())
    );
    if (header) {
      const lastHeaderIndex = header.reduce(
        (lastIndex, cell, index) =>
          String(cell || "").trim() ? index : lastIndex,
        -1
      );

      if (lastHeaderIndex >= 0 && isTotalsLine(header[lastHeaderIndex])) {
        table.forEach((row) => {
          row.splice(lastHeaderIndex, 1);
        });
      }
    }

    const worksheet = XLSX.utils.aoa_to_sheet(table);
    return XLSX.utils
      .sheet_to_csv(worksheet, { FS: delimiter, RS: "\r\n" })
      .replace(/\r?\n$/g, "")
      .split(/\r?\n/);
  } catch (error) {
    console.warn("Could not strip Totals from text export:", error);
    return lines;
  }
};

const getSortedTextLines = (list, fieldName) =>
  (Array.isArray(list) ? list : [])
    .map((r, index) => ({
      id: Number(r?.id ?? index),
      line: (r?.[fieldName] ?? "").toString(),
    }))
    .filter((x) => x.line && !isTotalsLine(x.line))
    .sort((a, b) => (a.id || 0) - (b.id || 0))
    .map((x) => x.line.replace(/\s+$/g, ""));

const getReportFileBaseName = (reportName) => {
  const cleaned = String(reportName || "report")
    .trim()
    .split("")
    .map((char) =>
      char.charCodeAt(0) < 32 || /[<>:"/\\|?*]/.test(char) ? "_" : char
    )
    .join("")
    .replace(/\s+/g, " ")
    .replace(/[. ]+$/g, "");

  return cleaned || "report";
};

const getReportCsvFileName = (reportName) =>
  `${getReportFileBaseName(reportName)}_report_${new Date()
    .toISOString()
    .slice(0, 10)}.csv`;

// wrapped in forwardRef to pass ref from parent (Reports.js) to child (Table.js)
const Table = forwardRef(
  ({ data, rows, cols, aggregatorName, vals, rendererName, reportName, showReportInfoIcon, onReportInfoClick, onAnalyticsFieldDrop }, ref) => {
    // IMPORTANT: data is initialized to [] so it is never undefined
    const [state, setState] = useState({
      rows: [],
      cols: [],
      aggregatorName: "Sum", // default aggregator
      vals: [],
      rendererName: "Table",
      reportName: "",
      data: [], // ensure exists from the start
      valueFilter: {},
      sorters: {},
      sorterCol: null,
      rowSorter: null,
      rowOrder: "key_a_to_z",
      colOrder: "key_a_to_z",
      derivedAttributes: {},
      enableRowSort: true,
      enableColSort: true,
    });

    // store updated vals in state
    const handleTableChange = (s) => {
      const layoutChanged =
        JSON.stringify(safeArray(s.rows)) !== JSON.stringify(safeArray(state.rows)) ||
        JSON.stringify(safeArray(s.cols)) !== JSON.stringify(safeArray(state.cols));

      setState((prev) => ({ ...prev, ...s }));

      if (
        layoutChanged &&
        String(state.reportName || reportName || "").toLowerCase().includes("analytics") &&
        onAnalyticsFieldDrop
      ) {
        onAnalyticsFieldDrop();
      }

      if (s.vals) {
        // console.log("Selected vals (sum_field):", s.vals);
      }
    };

    // define all possible fields expected in data
    // uključili smo id i row zbog QB Desktop exporta
    const allFields = [
      "id",
      "row",
      "transaction_date",
      "account_id",
      "account_description",
      "debit",
      "credit",
      "debit_credit_amount",
      "posting_comment",
      "xoption",
      "revenue_status",
    ];

    // update state when data/props change
    useEffect(() => {
      // normalize incoming prop data (could be [] or undefined)
      const incoming = Array.isArray(data) ? data : [];

      if (incoming.length > 0) {
        const normalizedData = incoming.map((row) => {
          const copy = {};

          Object.entries(row || {}).forEach(([key, value]) => {
            copy[key] = normalizePivotValue(value);
          });

          allFields.forEach((field) => {
            if (!(field in copy)) copy[field] = null;
          });
          return copy;
        });

        setState((prevState) => ({
          ...prevState,
          rows: safeArray(rows),
          cols: safeArray(cols),
          aggregatorName: aggregatorName || "Sum",
          vals: safeArray(vals),
          rendererName: rendererName || "Table",
          reportName: reportName || "",
          data: normalizedData, // set data
          enableRowSort: true,
          enableColSort: true,
        }));
      } else {
        // when no data, create placeholder row with all fields set to null
        const placeholderRow = {};
        allFields.forEach((field) => (placeholderRow[field] = null));

        setState((prevState) => ({
          ...prevState,
          rows: safeArray(rows),
          cols: safeArray(cols),
          aggregatorName: aggregatorName || "Sum",
          vals: safeArray(vals),
          rendererName: rendererName || "Table",
          reportName: reportName || "",
          data: [placeholderRow], // use a placeholder row for data
          enableRowSort: true,
          enableColSort: true,
        }));
      }
    }, [data, rows, cols, aggregatorName, vals, rendererName, reportName]);

    // expose methods to the parent (Reports.js)
    useImperativeHandle(ref, () => ({
      export: (options = {}) => {
        const {
          rows,
          cols,
          vals,
          aggregatorName,
          data,
          valueFilter,
          sorters,
          rowOrder,
          colOrder,
          derivedAttributes,
        } = state;
        const includeTotals = Boolean(options.includeTotals);
        const dataForPivot = Array.isArray(data) ? data : [];

        const pivotData = new PivotData({
          data: dataForPivot,
          rows,
          cols,
          vals,
          aggregatorName, // used from import from ./Utilities
          valueFilter,
          sorters,
          rowOrder,
          colOrder,
          derivedAttributes,
        });
        const rowAttrTotals = getRowAttrTotals(pivotData, rows);
        const totalLabelField =
          rows.find((row) => !(row in rowAttrTotals)) || rows[0];
        const grandTotalAggregator = pivotData.getAggregator([], []);

        // fields included in the export
        const fieldsToExport = includeTotals
          ? [...rows, ...cols, ...vals, "Totals"]
          : [...rows, ...cols, ...vals];

        // format data without Totals row data duplication
        const exportData = pivotData.getRowKeys().map((rowKey) => {
          const rowData = {};

          // row fields
          rows.forEach((row, index) => {
            rowData[row] = rowKey[index] ?? null;
          });

          // column fields (blank at row-level export)
          cols.forEach((col) => {
            rowData[col] = null;
          });

          // value fields
          vals.forEach((val) => {
            const aggregator = pivotData.getAggregator(rowKey, []);
            rowData[val] = normalizeExportNumber(aggregator.value());
          });

          if (includeTotals) {
            const aggregator = pivotData.getAggregator(rowKey, []);
            rowData["Totals"] = normalizeExportNumber(aggregator.value());
          }

          return rowData;
        });

        if (includeTotals) {
          // Calculate and append the last Totals row
          const totalsRow = {};
          fieldsToExport.forEach((field) => {
            if (field in rowAttrTotals) {
              totalsRow[field] = normalizeExportNumber(rowAttrTotals[field]);
            } else if (field === "Totals") {
              totalsRow[field] = normalizeExportNumber(grandTotalAggregator.value());
            } else if (vals.includes(field)) {
              totalsRow[field] = normalizeExportNumber(
                exportData.reduce(
                  (sum, row) => sum + normalizeExportNumber(row[field]),
                  0
                )
              );
            } else {
              totalsRow[field] = field === totalLabelField ? "Totals" : null;
            }
          });
          exportData.push(totalsRow);
        }

        // Convert data to Excel
        const worksheet = XLSX.utils.json_to_sheet(exportData);
        const workbook = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(workbook, worksheet, "Report");

        // Download as report.xlsx
        XLSX.writeFile(workbook, "report.xlsx");
      },

      exportToCSV: (reportName, options = {}) => {
        const {
          rows,
          cols,
          vals,
          aggregatorName,
          data,
          valueFilter,
          sorters,
          rowOrder,
          colOrder,
          derivedAttributes,
        } = state;
        const name = reportName || state?.reportName || "";
        const fileBaseName = getReportFileBaseName(name);
        const csvFileName = getReportCsvFileName(name);
        const includeTotals = Boolean(options.includeTotals);
        const skipCsvTotals = !includeTotals;

        console.log("Exporting to CSV, report name:", name);
        console.log("isQBDesktopMode:", isQBDesktopMode(name, data) );
        console.log("isSageMode:", isSageMode (name, data) );

        // ✅ QB Desktop posebni export: SAMO "row" -> .iif (TSV)
        if (isQBDesktopMode(name, data)) {
          const list = Array.isArray(data) ? data : [];

          // 1) uzmi samo row (string), 2) baci Totals, 3) sortiraj po id, 4) trim, 5) očisti eventualne zareze na kraju
          const rowLines = getSortedTextLines(list, "row");
          const lines = skipCsvTotals
            ? stripTotalsFromTextLines(rowLines)
            : rowLines;

          // nema dedupe → zadrži sve redke točno onako kako dolaze
          const content = lines.join("\r\n");

          // spremi kao .iif (tab-delimited text)
          downloadText(
            `${fileBaseName}.iif`,
            content
          );
          return;
        }

        // ✅ SAGE posebni export: standardni CSV s izvornim poljima (bez pivota)
        if (isSageMode(name, data)) {
          const list = Array.isArray(data) ? data : [];

          // 1) uzmi samo sage_row (string), 2) baci Totals, 3) sortiraj po id, 4) trim, 5) očisti eventualne zareze na kraju
          const sageLines = getSortedTextLines(list, "sage_row").map((line) =>
            line.replace(/,+$/g, "")
          );
          const lines = skipCsvTotals
            ? stripTotalsFromTextLines(sageLines)
            : sageLines;

          // nema dedupe → zadrži sve redke točno onako kako dolaze
          const content = lines.join("\r\n");

          // ekstenzija po želji (.txt / .csv / .iif)
          downloadText(
            `${fileBaseName}.txt`,
            content
          );
          return;
        }


        // 🔁 Default CSV export preko pivota
        const dataForPivot = Array.isArray(data) ? data : [];
        const pivotData = new PivotData({
          data: dataForPivot,
          rows,
          cols,
          vals,
          aggregatorName,
          valueFilter,
          sorters,
          rowOrder,
          colOrder,
          derivedAttributes,
        });
        const rowAttrTotals = getRowAttrTotals(pivotData, rows);
        const totalLabelField =
          rows.find((row) => !(row in rowAttrTotals)) || rows[0];
        const grandTotalAggregator = pivotData.getAggregator([], []);

        // Row reports already contain complete CSV lines in the "row" field.
        // Export only those lines, without pivot headers/id/Total/Totals columns.
        if (hasRowField(rows)) {
          const rowLines = getSortedTextLines(data, "row");
          const lines = skipCsvTotals
            ? stripTotalsFromTextLines(rowLines)
            : rowLines;
          const content = lines.join("\r\n");

          downloadText(
            csvFileName,
            content
          );
          return;
        }

        // fields included in the CSV
        const fieldsToExport = skipCsvTotals ? [...rows, ...cols] : [...rows, ...cols, "Totals"];

        // format data with calculated totals (basic example)
        const exportData = pivotData.getRowKeys().map((rowKey) => {
          const rowData = {};

          // row fields
          rows.forEach((row, index) => {
            rowData[row] = rowKey[index] ?? null;
          });

          // column fields left blank
          cols.forEach((col) => {
            rowData[col] = null;
          });

          if (!skipCsvTotals) {
            // Simple "Totals": koristimo agregator value()
            const aggr = pivotData.getAggregator(rowKey, []);
            rowData["Totals"] = normalizeExportNumber(aggr.value());
          }

          return rowData;
        });

        if (!skipCsvTotals) {
          // Append a bottom Totals row (sum of "Totals" column)
          const totalsRow = {};
          fieldsToExport.forEach((field) => {
            if (field in rowAttrTotals) {
              totalsRow[field] = normalizeExportNumber(rowAttrTotals[field]);
            } else if (field === "Totals") {
              totalsRow[field] = normalizeExportNumber(grandTotalAggregator.value());
            } else {
              totalsRow[field] = field === totalLabelField ? "Totals" : null;
            }
          });
          exportData.push(totalsRow);
        }

        // Convert data to CSV
        const worksheet = XLSX.utils.json_to_sheet(exportData);
        const csv = XLSX.utils.sheet_to_csv(worksheet);

        // Create BLOB for download
        const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
        const url = URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.setAttribute("href", url);
        link.setAttribute("download", csvFileName);
        link.style.visibility = "hidden";
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
      },

      getColumns: () => {
        const { rows, cols, vals } = state;
        return {
          rows: [...rows],
          cols: [...cols],
          vals: [...(vals || [])],
        };
      },
    }));

    // Choose safest data source for PivotTableUI
    const tableData =
      (Array.isArray(state.data) && state.data.length > 0 && state.data) ||
      (Array.isArray(data) && data.length > 0 && data) ||
      [{}];

    return (
      <StyledCard>
        <Container>
          {showReportInfoIcon && (
            <ReportActionsHeader>
              <ReportInfoIcon type="button" title="Validate Report" aria-label="Validate Report" onClick={onReportInfoClick}>
                Validate
              </ReportInfoIcon>
            </ReportActionsHeader>
          )}
          <PivotTableUI
            unusedOrientationCutoff={Infinity}
            menuLimit={Infinity}
            data={tableData}
            onChange={handleTableChange}
            renderers={TableRenderers}
            // Explicitly pass only the keys PivotTableUI needs (avoid overriding "data")
            rows={state.rows}
            cols={state.cols}
            aggregatorName={state.aggregatorName}
            vals={state.vals}
            rendererName={state.rendererName}
            reportName={state.reportName}
            valueFilter={state.valueFilter}
            sorters={state.sorters}
            sorterCol={state.sorterCol}
            rowSorter={state.rowSorter}
            rowOrder={state.rowOrder}
            colOrder={state.colOrder}
            derivedAttributes={state.derivedAttributes}
            enableRowSort={state.enableRowSort}
            enableColSort={state.enableColSort}
          />
        </Container>
      </StyledCard>
    );
  }
);

export default Table;

